"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { X, Loader2 } from "lucide-react";
import Image from "next/image";
import useTozy from "@/app/lib/features/tozy/tozy.hook";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { SendMessageResponse } from "@/app/lib/features/tozy/tozy.types";
import TozyMessageBubble from "@/app/components/common/tozy/TozyMessageBubble";
import TozyTypingIndicator from "@/app/components/common/tozy/TozyTypingIndicator";
import TozyEmptyState from "@/app/components/common/tozy/TozyEmptyState";
import TozyInput from "@/app/components/common/tozy/TozyInput";
import { trackGeneric } from "@/app/lib/tracking/interactions";

interface TozyDialogProps {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  icon: string;
  onClose: () => void;
}

const TozyDialog = ({ triggerRef, icon, onClose }: TozyDialogProps) => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= 640
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const {
    control,
    isControlLoading,
    messages,
    hasMore,
    isMessagesLoading,
    isSending,
    fetchControl,
    fetchMessages,
    fetchMore,
    send,
  } = useTozy();
  const { isAuthenticated } = useAuth();

  const [input, setInput] = useState("");
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [newMessageId, setNewMessageId] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);   // the scrollable container
  const contentRef = useRef<HTMLDivElement>(null);  // inner content div watched by ResizeObserver
  const dialogRef = useRef<HTMLDivElement>(null);
  const hasFetchedHistory = useRef(false);

  // "pinned" = auto-scroll to bottom whenever content grows
  const pinned = useRef(true);

  // ─── Scroll helpers ──────────────────────────────────────────────────────────

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const isAtBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  // ─── ResizeObserver: scroll whenever content height grows, if pinned ─────────

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const ro = new ResizeObserver(() => {
      if (pinned.current) scrollToBottom("instant");
    });

    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollToBottom]);

  // ─── Wheel: unpin immediately on upward scroll ────────────────────────────────

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) pinned.current = false;
    };

    el.addEventListener("wheel", onWheel, { passive: true });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // ─── Scroll event: re-pin when user scrolls back to bottom ───────────────────

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (isAtBottom()) {
      pinned.current = true;
    }

    // Load history when near top
    if (el.scrollTop < 80 && !isMessagesLoading && hasMore) fetchMore();
  }, [isAtBottom, isMessagesLoading, hasMore, fetchMore]);

  // ─── New message sent: pin + jump to bottom ───────────────────────────────────

  useEffect(() => {
    if (!pendingMessage) return;
    pinned.current = true;
    scrollToBottom("instant");
  }, [pendingMessage, scrollToBottom]);

  // ─── Initial data fetch ───────────────────────────────────────────────────────

  useEffect(() => {
    fetchControl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated || hasFetchedHistory.current) return;
    hasFetchedHistory.current = true;
    fetchMessages({ per_page: 20, page: 1 });
  }, [isAuthenticated, fetchMessages]);

  // ─── Click outside / Escape ───────────────────────────────────────────────────

  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dialogRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose, triggerRef]);

  // ─── Send ─────────────────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isSending) return;
    trackGeneric("tozy_message", "click");
    setInput("");
    setPendingMessage(text);

    const action = await send({ message: text, version: "v1" });
    setPendingMessage(null);

    if (action.meta.requestStatus === "fulfilled") {
      const data = (action.payload as SendMessageResponse).data;
      if (data?.id) setNewMessageId(data.id);
    }
  }, [input, isSending, send]);

  const handleSuggestion = useCallback((text: string) => {
    setInput(text);
  }, []);

  const isBlocked = !isControlLoading && control?.control === 1;
  const isEmpty =
    messages.length === 0 && !isMessagesLoading && !isSending && !pendingMessage;

  return (
    <div
      ref={dialogRef}
      className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col animate-in fade-in duration-200"
      style={
        isDesktop
          ? { right: 64, bottom: 24, width: 460, height: 600 }
          : { left: 12, right: 12, bottom: 12, height: "85dvh" }
      }
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="relative shrink-0">
          <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center overflow-hidden">
            <Image src={icon} alt="Tozzy" width={22} height={22} className="object-contain" />
          </div>
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
              isControlLoading ? "bg-amber-400 animate-pulse" : "bg-emerald-400"
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm leading-none">Tozzy</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {isControlLoading ? "Yükleniyor..." : "AI Alışveriş Asistanı · Çevrimiçi"}
          </p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      {isBlocked ? (
        <div
          className="flex-1 flex items-center justify-center p-5 text-sm text-gray-500 text-center"
          dangerouslySetInnerHTML={{ __html: control!.message ?? "" }}
        />
      ) : (
        <>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto min-h-0"
          >
            {/* contentRef wraps everything so ResizeObserver tracks full height */}
            <div ref={contentRef} className="px-4 py-4 space-y-4">
              {isMessagesLoading && (
                <div className="flex justify-center py-3">
                  <Loader2 className="w-4 h-4 text-gray-300 animate-spin" />
                </div>
              )}

              {isEmpty && (
                <TozyEmptyState icon={icon} onSuggestionClick={handleSuggestion} />
              )}

              {messages.map((msg) => (
                <TozyMessageBubble
                  key={msg.id === newMessageId ? `${msg.id}-new` : msg.id}
                  msg={msg}
                  isNew={msg.id === newMessageId}
                  icon={icon}
                />
              ))}

              {pendingMessage && (
                <TozyTypingIndicator icon={icon} pendingMessage={pendingMessage} />
              )}
            </div>
          </div>

          <TozyInput
            value={input}
            onChange={setInput}
            onSend={handleSend}
            isSending={isSending}
            isAuthenticated={isAuthenticated}
          />
        </>
      )}
    </div>
  );
};

export default TozyDialog;